import {
  EmptyCell,
  Typography,
  Avatar,
  Icon,
  ItemsWithCount,
  Status,
  Tooltip,
  TooltipLink,
} from 'shared/ui'
import { type IColumn } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib'
import { InboxCell } from 'entities/Inbox/ui/InboxCell/InboxCell'
import { EnumUserRoles } from 'entities/Users'
import { type RowUser } from 'entities/Users/model/Table/RowUser'
import { BillingPath } from 'entities/ExportRouters'
import { RoleChip } from 'pages/settings/pages/members/ui/RoleChip/RoleChip'
import styles from './styles.module.scss'

export const getMembersColumns = (): IColumn<RowUser>[] => [
  {
    field: 'fullName',
    name: 'Name',
    isSortable: true,
    sortKey: 'full_name',
    isDisabledChangeVisible: true,
    maxWidth: '250px',
    width: '100%',
    renderRowCell: (row) => {
      return (
        <div className={styles.name}>
          {row.isPaygLocked && (
            <Tooltip
              label={
                <>
                  You have reached the limit of 1 member on Pay
                  <br />
                  As You Go plan. Upgrade to remove the limits. <br />
                  <TooltipLink
                    text={'Upgrade'}
                    onClick={() => {
                      uiStore.changeRouteViaVue({
                        path: `/${BillingPath.upgrade}`,
                      })
                    }}
                  />
                </>
              }
              placement={'top'}
            >
              <Icon icon={'lock'} fontSize={20} color='var(--content-primary-tertiary)' />
            </Tooltip>
          )}
          <Avatar
            info={row.avatarInfo}
            size={32}
            fontSize={14}
            disabled={row.isPaygLocked}
            online={!row.isAway}
          />
          <div className={styles.nameInfo}>
            <Typography
              variant='body-md-regular'
              ariaLabel='MemberFullName'
              tooltipProps={{
                label: row.fullName,
              }}
              ellipsis
            >
              {row.fullName}
            </Typography>
            <Typography
              variant='body-sm-regular'
              ariaLabel='MemberEmail'
              color='var(--content-primary-tertiary)'
              tooltipProps={{
                label: row.email,
              }}
              ellipsis
            >
              {row.email}
            </Typography>
          </div>
        </div>
      )
    },
  },
  {
    field: 'roleLabel',
    name: 'Role',
    isSortable: true,
    sortKey: 'role',
    isDisabledChangeVisible: true,
    maxWidth: '100px',
    renderRowCell: (row) => (
      <RoleChip
        roleLabel={row.roleLabel}
        hasCustomPermissions={row.hasCustomPermissions}
        disabled={row.isPaygLocked}
      />
    ),
  },
  {
    field: 'seat',
    name: 'Seat',
    minWidth: '60px',
    renderRowCell: (row) =>
      row.role !== EnumUserRoles.VIEW_ONLY ? (
        <Icon
          icon='check1'
          color={
            row.isPaygLocked ? 'var(--content-primary-disabled)' : 'var(--content-brand-primary)'
          }
        />
      ) : (
        <EmptyCell />
      ),
  },
  {
    field: 'inboxes',
    name: 'Inboxes',
    width: '232px',
    maxWidth: '232px',
    renderRowCell: (row) => {
      if (!row.inboxes.length) {
        return <EmptyCell />
      }

      return (
        <ItemsWithCount
          items={row.inboxes}
          disabled={row.isPaygLocked}
          itemComponent={({ item }) => (
            <InboxCell id={item.id} name={item.name} className={styles.inboxCellContainer} />
          )}
          width={190}
          containerWidth={216}
          cacheScope={'member-inboxes'}
          dropdownItemRenderer={(item, index) => ({
            id: index,
            label: item.name,
            onClick: () => {
              uiStore.changeRouteViaVue({
                path: `/settings/organization/inboxes/all-inboxes/${item.id}`,
              })
            },
            parentTooltipProps: {
              label: item.name,
              placement: 'left',
              fullWidth: true,
            },
          })}
        />
      )
    },
  },
  {
    field: 'twoFaEnabled',
    name: '2FA',
    minWidth: '100px',
    renderRowCell: (row) =>
      row.twoFaEnabled ? (
        <Status
          title={'Active'}
          intent={'positive'}
          emphasis={'high'}
          icon={'check2'}
          disabled={row.isPaygLocked}
        />
      ) : (
        <Status
          title={'Disabled'}
          intent={'neutral'}
          emphasis={'high'}
          icon={'minusCircle'}
          disabled={row.isPaygLocked}
        />
      ),
  },
  {
    field: 'createdAt',
    name: 'Added',
    maxWidth: '160px',
    renderRowCell: (row) => row.createdAt && uiStore.dayjs(row.createdAt).format(DayjsFormats.full),
  },
]
